import { X, ClipboardList, Info, ChevronRight, Zap } from 'lucide-react';

interface EditableModalProps {
  isOpen: boolean;
  heading: string;
  inputHeading1: string;
  inputHeading2: string;
  value: { title: string; description: string };
  onCancel: () => void;
}

const EditableModal = ({ isOpen, heading, inputHeading1, inputHeading2, value, onCancel }: EditableModalProps) => {
  if (!isOpen) return null;

  const formatContent = (content: any) => {
    if (!content) return { isJson: false, content: 'No data available' };
    
    if (typeof content === 'object') {
      return { isJson: true, content: JSON.stringify(content, null, 2) };
    }

    const contentStr = String(content);
    
    try {
      const parsed = JSON.parse(contentStr);
      return { isJson: true, content: JSON.stringify(parsed, null, 2) };
    } catch {
      return { isJson: false, content: contentStr.trim() };
    }
  };

  const renderJsonContent = (jsonString: string) => {
    return jsonString.split('\n').map((line, i) => {
      const coloredLine = line
        .replace(/"([^"]+)":/g, '<span class="text-indigo-400">"$1"</span><span class="text-slate-500">:</span>')
        .replace(/: "([^"]+)"/g, ': <span class="text-emerald-400">"$1"</span>')
        .replace(/: (true|false)/g, ': <span class="text-orange-400 font-black">$1</span>')
        .replace(/: (\d+)/g, ': <span class="text-amber-400 font-mono">$1</span>')
        .replace(/([{}\[\]])/g, '<span class="text-slate-500 font-bold">$1</span>')
        .replace(/(,)/g, '<span class="text-slate-600">$1</span>');
      
      return (
        <div key={i} className="leading-6 font-mono text-[11px] whitespace-pre" dangerouslySetInnerHTML={{ __html: coloredLine }} />
      );
    });
  };

  const renderMarkdownContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      let content = line.trim();
      if (!content) return <div key={i} className="h-2" />;

      if (content.startsWith('## ')) {
        return (
          <h4 key={i} className="text-white text-xs font-black uppercase tracking-[0.2em] mt-6 mb-3 flex items-center gap-2 border-l-4 border-indigo-500 pl-3 bg-indigo-500/5 py-1 rounded-r-lg">
            {content.replace('## ', '')}
          </h4>
        );
      }

      if (content.startsWith('- ')) {
        const isSatisfied = content.includes('[x]') || content.startsWith('- [');
        return (
          <div key={i} className="flex gap-2.5 items-start mb-2 group">
            <span className={`mt-1 flex-shrink-0 ${isSatisfied ? 'text-emerald-400' : 'text-slate-600'}`}>
              <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
            <p className="text-[11px] text-slate-300 leading-relaxed font-medium">
              {content.replace('- ', '').replace('[x]', '✅').replace('[ ]', '⬜')}
            </p>
          </div>
        );
      }

      if (content.startsWith('+') || content.startsWith('-')) {
        const isPositive = content.startsWith('+');
        return (
          <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg mb-2 text-[10px] font-black uppercase tracking-wider border ${
            isPositive ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}>
            <Zap size={10} fill="currentColor" />
            {content}
          </div>
        );
      }

      return (
        <p key={i} className="text-[11px] text-slate-400 leading-relaxed font-medium mb-1">
          {content}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-fade-in">
      <div className="bg-slate-900 border border-slate-700/50 w-full max-w-6xl max-h-[90vh] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col scale-in">
        {/* Header */}
        <div className="p-8 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-500/20 text-indigo-400 rounded-2xl border border-indigo-500/20 shadow-inner">
              <ClipboardList size={28} strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
                {heading}
                <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-[10px] text-slate-500 font-black uppercase tracking-widest translate-y-[-2px]">v2.0</span>
              </h2>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">Match Making Intelligence & Insight Report</p>
            </div>
          </div>
          <button 
            onClick={onCancel}
            className="p-3 hover:bg-slate-800 rounded-2xl text-slate-500 hover:text-white transition-all active:scale-90"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row p-8 gap-8">
          {/* Left Column: Data Payload */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">{inputHeading1}</h3>
              </div>
              <span className="text-[9px] font-black text-indigo-500/70 border border-indigo-500/20 px-2 py-0.5 rounded uppercase tracking-tighter">Raw Analysis</span>
            </div>
            <div className="flex-1 bg-slate-950/80 border border-slate-800/80 rounded-3xl p-6 overflow-y-auto custom-scrollbar shadow-inner group">
              <div className="text-slate-400 opacity-60 group-hover:opacity-100 transition-opacity">
                {(() => {
                  const formatted = formatContent(value.title);
                  return formatted.isJson ? renderJsonContent(formatted.content) : 
                    <p className="font-mono text-[11px] whitespace-pre-wrap leading-6">{formatted.content}</p>;
                })()}
              </div>
            </div>
          </div>

          {/* Right Column: Narrative Commentary */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">{inputHeading2}</h3>
              </div>
              <span className="text-[9px] font-black text-emerald-500/70 border border-emerald-500/20 px-2 py-0.5 rounded uppercase tracking-tighter">Match Insights</span>
            </div>
            <div className="flex-1 bg-slate-900 border border-slate-800/80 rounded-3xl p-8 overflow-y-auto custom-scrollbar shadow-2xl relative">
              <div className="absolute top-0 right-0 p-6 pointer-events-none opacity-5">
                <Info size={120} className="text-indigo-500" />
              </div>
              <div className="relative z-10">
                {renderMarkdownContent(String(value.description || ''))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 bg-slate-950 border-t border-slate-800 flex justify-end items-center gap-6">
          <div className="flex-1 flex items-center gap-3 text-slate-600">
             <Info size={14} className="text-indigo-500" />
             <span className="text-[9px] font-bold uppercase tracking-widest">Confidential Match Information • Authorized Admin Access Only</span>
          </div>
          <button
            onClick={onCancel}
            className="px-10 py-3 bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl border border-slate-700 transition-all active:scale-95 shadow-xl"
          >
            Close Insight
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditableModal;

